import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { getJSON } from '@/lib/api'
import {
  displayPortalDate,
  type MemberPortalMembership as Membership,
} from './memberPortalPresentation'

type LevelChangeQuote = {
  target: { name: string; price: string; price_label?: string }
  credit: { label: string; amount: string }
  due_today: string
  renewal_due_on: string
}

export function LevelChangeSection({ membership }: { membership: Membership }) {
  const targets = membership.level_change_targets ?? []
  const [selectedId, setSelectedId] = React.useState<number | null>(targets[0]?.id ?? null)
  const [quote, setQuote] = React.useState<LevelChangeQuote | null>(null)
  const quoteRequest = useMutation({
    mutationFn: (targetId: number) =>
      getJSON<{ level_change_quote: LevelChangeQuote }>(
        `/api/v1/member_portal/level_change_quote?target_level_id=${targetId}`,
      ),
    onSuccess: ({ level_change_quote }) => setQuote(level_change_quote),
  })

  if (membership.status === 'Pending-Renewal') {
    return (
      <section className="mt-6 space-y-3">
        <h2 className="text-body font-semibold">Change level</h2>
        <Alert variant="warning">
          Level changes are unavailable while a renewal Invoice is open.
        </Alert>
      </section>
    )
  }

  if (quote) return <LevelChangePreview quote={quote} onBack={() => setQuote(null)} />

  return (
    <section className="mt-6 space-y-3">
      <h2 className="text-body font-semibold">Change level</h2>
      {targets.length === 0 ? (
        <Card className="p-4">
          <p className="text-body-sm text-muted-foreground">
            Membership changes are handled by the association.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {targets.map((target) => (
            <button
              key={target.id}
              type="button"
              className="flex w-full items-center justify-between gap-4 border-b border-border p-4 text-left transition-colors last:border-b-0 hover:bg-accent"
              onClick={() => setSelectedId(target.id)}
            >
              <span className="flex min-w-0 items-center gap-3">
                <span
                  className={`flex size-4 shrink-0 items-center justify-center rounded-full border ${selectedId === target.id ? 'border-primary' : 'border-input'}`}
                  aria-hidden="true"
                >
                  {selectedId === target.id && <span className="size-2 rounded-full bg-primary" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-body-sm font-medium">{target.name}</span>
                  <span className="block text-caption text-muted-foreground">
                    {target.description ?? 'Change your membership level.'}
                  </span>
                </span>
              </span>
              <span className="shrink-0 text-body-sm font-semibold tabular-nums">
                {target.price_label ?? target.price}
              </span>
            </button>
          ))}
          <div className="flex justify-end bg-muted/50 p-4">
            <Button
              type="button"
              size="sm"
              disabled={selectedId === null || quoteRequest.isPending}
              onClick={() => selectedId && quoteRequest.mutate(selectedId)}
            >
              {quoteRequest.isPending ? 'Loading preview...' : 'Continue'}
            </Button>
          </div>
          {quoteRequest.isError && (
            <p className="px-4 pb-4 text-caption text-destructive">
              The level-change preview is currently unavailable.
            </p>
          )}
        </Card>
      )}
    </section>
  )
}

function LevelChangePreview({ quote, onBack }: { quote: LevelChangeQuote; onBack: () => void }) {
  return (
    <section className="mt-6 max-w-xl space-y-3">
      <Button
        type="button"
        variant="link"
        className="h-auto p-0 text-muted-foreground"
        onClick={onBack}
      >
        ← Change level
      </Button>
      <Card className="overflow-hidden">
        <div className="p-4">
          <h2 className="text-body font-semibold">Switch to {quote.target.name}</h2>
          <p className="mt-1 text-caption text-muted-foreground">
            Effective today · your renewal becomes {displayPortalDate(quote.renewal_due_on)}
          </p>
        </div>
        <div className="space-y-3 border-t border-border p-4 text-body-sm">
          <div className="flex justify-between gap-6">
            <span>{quote.target.name} membership — 1 year</span>
            <span className="shrink-0 text-right font-semibold tabular-nums">
              {quote.target.price_label ?? quote.target.price}
            </span>
          </div>
          <div className="flex justify-between gap-6 text-muted-foreground">
            <span>{quote.credit.label}</span>
            <span className="shrink-0 text-right tabular-nums">−{quote.credit.amount}</span>
          </div>
          <div className="flex justify-between gap-6 border-t border-border pt-3 text-body font-semibold">
            <span>Due today</span>
            <span className="shrink-0 text-right tabular-nums">{quote.due_today}</span>
          </div>
        </div>
      </Card>
    </section>
  )
}
